import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '@/context/SessionContext';
import LearnerLayout from '@/components/LearnerLayout';
import { Button } from '@/components/ui/button';
import {
  BookOpen, ChevronLeft, ChevronRight, Check, Lock, Loader2, Layers, GitBranch
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SequenceCard = ({ seq, onOpen }) => {
  const firstLetter = (seq.title || 'S')[0].toUpperCase();
  const progress = seq.progress || 0;
  const isComplete = progress >= 100;
  const isStarted = progress > 0;

  return (
    <div className="w-56 shrink-0 rounded-2xl overflow-hidden bg-white shadow-md hover:shadow-lg transition-shadow cursor-pointer" onClick={() => onOpen(seq)} data-testid={`seq-card-${seq.seq_id}`}>
      <div className="relative h-44 bg-gradient-to-br from-indigo-300 to-indigo-400 flex items-center justify-center">
        <span className="text-6xl font-light text-white/60">{firstLetter}</span>
        {isComplete && (
          <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center shadow-lg" data-testid="completion-badge">
            <Check className="h-5 w-5 text-white" />
          </div>
        )}
      </div>
      <div className="p-4">
        <p className="text-sm font-semibold text-slate-900 leading-snug line-clamp-2 min-h-[40px]">{seq.title}</p>
        <div className="flex items-center gap-2 mt-3">
          <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-blue-600 rounded-full transition-all" style={{ width: `${progress}%` }} />
          </div>
          <span className="text-xs text-slate-500 shrink-0">{progress} %</span>
        </div>
        <Button
          className={`mt-3 text-xs font-bold rounded-md h-8 px-4 ${isComplete ? 'bg-blue-700 hover:bg-blue-800 text-white' : isStarted ? 'bg-blue-700 hover:bg-blue-800 text-white' : 'bg-slate-200 hover:bg-blue-700 hover:text-white text-slate-600'}`}
          data-testid={`seq-action-${seq.seq_id}`}
        >
          {isComplete ? 'Consulter' : isStarted ? 'Continuer' : 'Commencer'}
        </Button>
      </div>
    </div>
  );
};

const HorizontalScroller = ({ children }) => {
  const ref = useRef(null);
  const scroll = (dir) => {
    if (ref.current) ref.current.scrollBy({ left: dir * 280, behavior: 'smooth' });
  };
  return (
    <div className="relative group">
      <button className="absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow" onClick={() => scroll(-1)}>
        <ChevronLeft className="h-5 w-5" />
      </button>
      <div ref={ref} className="flex gap-5 overflow-x-auto pb-4 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        {children}
      </div>
      <button className="absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-blue-700 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow" onClick={() => scroll(1)}>
        <ChevronRight className="h-5 w-5" />
      </button>
    </div>
  );
};

const LearnerSequences = () => {
  const navigate = useNavigate();
  const { selectedSessionId, currentCourse } = useSession();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!selectedSessionId) { setLoading(false); return; }
    setLoading(true);
    fetch(`${API}/learner/sessions/${selectedSessionId}/elearning`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setData(d); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [selectedSessionId]);

  const openSequence = (seq) => navigate(`/apprenant/sequences/${seq.seq_id}`);

  if (loading) {
    return <LearnerLayout><div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div></LearnerLayout>;
  }

  const courseTitle = data?.course_title || currentCourse?.title || 'Formation';
  const sequences = data?.sequences || [];
  const parcoursList = data?.parcours || [];
  const horsParcoursSeqs = sequences.filter(s => !s.parcours_id);
  const overallProgress = data?.overall_progress || 0;

  return (
    <LearnerLayout>
      <div className="space-y-8" data-testid="learner-sequences-page">
        {/* Breadcrumb */}
        <p className="text-sm text-slate-400">{courseTitle} - Mes séquences elearning</p>

        {/* Title */}
        <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900" data-testid="sequences-title">Mes séquences elearning</h1>

        {/* Stats bar */}
        <div className="bg-indigo-50/70 rounded-2xl px-8 py-5 flex items-center gap-10 flex-wrap" data-testid="stats-bar">
          {/* Progression */}
          <div className="flex items-center gap-3">
            <div className="relative w-12 h-12">
              <svg className="w-12 h-12 -rotate-90" viewBox="0 0 48 48">
                <circle cx="24" cy="24" r="19" fill="none" stroke="#e0e7ff" strokeWidth="4" />
                <circle cx="24" cy="24" r="19" fill="none" stroke="#22c55e" strokeWidth="4"
                  strokeDasharray={`${(overallProgress / 100) * 119.4} 119.4`} strokeLinecap="round" />
              </svg>
            </div>
            <div>
              <p className="text-xs text-slate-500">Progression de la formation</p>
              <p className="text-xl font-bold text-emerald-600">{overallProgress}%</p>
            </div>
          </div>
          {/* Nombre de parcours */}
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
              <GitBranch className="h-5 w-5 text-indigo-500" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Nombre de parcours</p>
              <p className="text-xl font-bold text-indigo-600">{data?.total_parcours || 0}</p>
            </div>
          </div>
          {/* Nombre de séquences */}
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
              <Layers className="h-5 w-5 text-indigo-500" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Nombre de séquences</p>
              <p className="text-xl font-bold text-indigo-600">{data?.total_sequences || 0}</p>
            </div>
          </div>
        </div>

        {/* Hors parcours */}
        {horsParcoursSeqs.length > 0 && (
          <div data-testid="hors-parcours-section">
            <HorizontalScroller>
              {horsParcoursSeqs.map(seq => <SequenceCard key={seq.seq_id} seq={seq} onOpen={openSequence} />)}
            </HorizontalScroller>
          </div>
        )}

        {/* Parcours sections */}
        {parcoursList.map(p => {
          const parcoursSeqs = sequences.filter(s => s.parcours_id === p.parcours_id);
          if (parcoursSeqs.length === 0) return null;
          return (
            <div key={p.parcours_id} className="space-y-4" data-testid={`parcours-section-${p.parcours_id}`}>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
                  <GitBranch className="h-5 w-5 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900 leading-snug">{p.name}</h2>
                  <p className="text-sm text-slate-500">{parcoursSeqs.length} séquences dans ce parcours (parcours séquentiel)</p>
                </div>
              </div>
              <HorizontalScroller>
                {parcoursSeqs.map((seq, i) => (
                  <SequenceCard key={seq.seq_id} seq={seq} onOpen={openSequence} />
                ))}
              </HorizontalScroller>
            </div>
          );
        })}

        {/* Empty state */}
        {sequences.length === 0 && (
          <div className="text-center py-16">
            <BookOpen className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Aucune séquence</h2>
            <p className="text-slate-600">Aucune séquence e-learning n'est configurée pour cette session.</p>
          </div>
        )}
      </div>
    </LearnerLayout>
  );
};

export default LearnerSequences;
